import React, { useState, useEffect } from 'react';
import { LayoutAnimation } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import {
  availableMachines,
  startMachineOnboardingSession,
  validateMachineOnboardingEndpoint,
  validateMachineOnboardingByAutomaticControl,
  validateMachineOnboardingByBrand,
  validateMachineOnboardingByConnectionMethod,
  addSelectedMachines,
  addMachine,
  clearMachines,
  getMachineBrand,
  getMachines,
  getMachineConnectionMethod,
  getMachineFromHistory,
  getDemoMachineType,
} from '../../../../../../../reducers/machines';

import {
  getConnectorIpRange,
  configureIpRangeForScan,
  removeIpRangeFromScan,
  requestIpRangeScan,
  connectorIpScanStatus,
  configureNetworkScan,
} from '../../../../../../../reducers/membership';

import ColorWrapper from '@styles/colors';
import { workerTypeStatusNames, workerType, Alert } from '@helpers';

import SearchMachines from './searchMachines';
import DemoMachines from './demoMachines';
import ChangeName from './changeName';
import EnterPassword from './enterPassword';
import AddIP from './addIP';
import AddShotName from './addShotName';
import MachineControl from './machineControl';
import MachineProtocol from './machineProtocol';
import MachineConnectionMethod from './machineConnectionMethod';
import SearchSettings from './searchSettings';
import IpRangeDetails from './ipRangeDetails';
import Connection from './connection';
import ConnectorStatus from './connectorStatus';
import SameMachine from './sameMachine';
import CreateIpRange from './createIpRange';

const MODES = {
  SEARCH_MACHINES: 'SEARCH_MACHINES',
  DEMO_MACHINES: 'DEMO_MACHINES',
  CHANGE_MACHINE_NAME: 'CHANGE_MACHINE_NAME',
  ADD_MACHINE_IP: 'ADD_MACHINE_IP',
  ADD_SHORT_NAME: 'ADD_SHORT_NAME',
  ENTER_PASSWORD: 'ENTER_PASSWORD',
  MACHINE_CONTROL: 'MACHINE_CONTROL',
  MACHINE_CONNECTION_METHOD: 'MACHINE_CONNECTION_METHOD',
  MACHINE_PROTOCOL: 'MACHINE_PROTOCOL',
  SEARCH_SETTINGS: 'SEARCH_SETTINGS',
  IP_RANGE_DETAILS: 'IP_RANGE_DETAILS',
  CREATE_IP_RANGE: 'CREATE_IP_RANGE',
  CONNECTION: 'CONNECTION',
  SAME_MACHINE: 'SAME_MACHINE',
};

const AddMachine = (props) => {
  const {
    availableMachines,
    hide,
    validateMachineOnboardingByAutomaticControl,
    validateMachineOnboardingByBrand,
    validateMachineOnboardingByConnectionMethod,
    connector,
    addMachine,
    getMachines,
    initialMode,
    initialData,
    isReOnboarding,
  } = props;

  const [mode, setModeValue] = useState(initialMode || MODES.SEARCH_MACHINES);

  const [modeHistory, setModeHistory] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);

  const ignoreHistoryPool = [MODES.CONNECTION];
  const setMode = (v) => {
    const last = modeHistory?.[modeHistory?.length - 1];
    if (!ignoreHistoryPool.includes(mode) && mode !== last && v !== last && v !== mode) {
      setModeHistory([...modeHistory, mode]);
    }
    if (last === v) {
      const history = [...modeHistory];
      history.pop();
      setModeHistory(history);
    }
    LayoutAnimation.easeInEaseOut();
    setModeValue(v);
  };

  const goBack = (storage) => {
    const history = [...(storage || modeHistory)];
    const last = history.pop();
    if (last === mode) {
      return goBack(history);
    }
    if (last) {
      LayoutAnimation.easeInEaseOut();
      setModeValue(last);
      setModeHistory(history);
    } else {
      hide();
    }
  };

  useEffect(() => {
    if (!isReOnboarding) {
      availableMachines();
    }
  }, []);

  const [data, setData] = useState(initialData || {});

  const updateData = (key, value) => {
    setData({
      ...data,
      [key]: value,
    });
  };

  const updateDataObj = (obj) => {
    setData({
      ...data,
      ...obj,
    });
  };

  const addMachineAction = async (data) => {
    const response = await addMachine(data);
    if (response.onboardedMachineFailureResult) {
      Alert({ title: response?.onboardedMachineFailureResult?.errorMessage || 'Error', type: 'danger' });
    }

    if (response.onboardedMachineSuccessResult) {
      Alert({ title: 'Success', type: 'success' });
      await getMachines();
      hide();
    }
  };

  const validateUserAction = async (machine) => {
    updateDataObj({ ...machine, error: false, success: false });
    setMode(MODES.CONNECTION);

    const resp = await (async () => {
      if (machine.automaticControl) {
        return (await validateMachineOnboardingByAutomaticControl(machine)) || {};
      }
      if (machine.machineBrandCode) {
        return (await validateMachineOnboardingByBrand(machine)) || {};
      }
      if (machine.connectionMethodCode) {
        return (await validateMachineOnboardingByConnectionMethod(machine)) || {};
      }

      return (await validateMachineOnboardingByAutomaticControl(machine)) || {};
    })();

    if (resp.credentialsFailureResult) {
      const machineCredentials = resp.credentialsFailureResult?.invalidMachineCredentials?.[0] || {};
      const isError = machineCredentials.userName || machineCredentials.password;

      if (isError) {
        Alert({
          title: i18n.t('Verification Failed'),
          text: i18n.t('Your user name or password is incorrect.'),
          actions: [
            {
              text: i18n.t('Ok'),
              onPress: () => console.log('Ok Pressed'),
              style: 'primary',
            },
          ],
        });
      }

      updateDataObj({
        ...machine,
        allowedToContinueWithWarning: resp.credentialsFailureResult?.allowedToContinueWithWarning,
        credentialsErrorMessage: resp.credentialsFailureResult.errorMessage,
        credentialTypeRequired: machineCredentials.machineCredentialType || 'CredentialForConnection',
        userName: machineCredentials.userName || '',
        password: machineCredentials.password || '',
      });

      setMode(MODES.ENTER_PASSWORD);
      return;
    }

    if (resp.machineOnboardingSessionNoFoundResult) {
      Alert({ title: 'Session not found', type: 'danger' });
      updateDataObj({ ...machine, suggestedConnectionMethod: null, error: true, success: false });
      return;
    }

    if (resp.connectionFailureResult) {
      updateDataObj({ ...machine, suggestedConnectionMethod: null, error: true, success: false });
      return;
    }

    if (resp.connectionConfirmationNeededResult) {
      updateDataObj({
        ...machine,
        suggestedConnectionMethod: resp.connectionConfirmationNeededResult.suggestedConnectionMethod,
        error: true,
        success: false,
      });
      return;
    }

    if (resp.automaticConnectionFailureResult) {
      updateDataObj({ ...machine, error: true, suggestedConnectionMethod: null });
      return;
    }

    if (resp.successResult) {
      const name = resp.successResult.suggestedMachineName;
      const ncFile = resp.successResult.ncFile;
      const newData = { ...machine, error: false, success: true, ncFile };

      if (!machine.machineId) {
        newData.fullName = name || '';
        newData.shortName = name ? name.slice(0, 20) : '';
      }

      updateDataObj(newData);
      return;
    }

    updateDataObj({ ...machine, suggestedConnectionMethod: null, error: true, success: false });
    return;
  };

  const renderComponent = () => {
    const p = {
      ...props,
      setMode,
      setData,
      MODES,
      updateData,
      updateDataObj,
      data,
      selectedRange,
      setSelectedRange,
      goBack,
      validateUserAction,
      addMachineAction,
    };

    switch (mode) {
      case MODES.SEARCH_MACHINES:
        return <SearchMachines {...p} />;
      case MODES.DEMO_MACHINES:
        return <DemoMachines {...p} />;
      case MODES.CHANGE_MACHINE_NAME:
        return <ChangeName {...p} />;
      case MODES.ENTER_PASSWORD:
        return <EnterPassword {...p} />;
      case MODES.MACHINE_CONTROL:
        return <MachineControl {...p} />;
      case MODES.ADD_MACHINE_IP:
        return <AddIP {...p} />;
      case MODES.ADD_SHORT_NAME:
        return <AddShotName {...p} />;
      case MODES.MACHINE_PROTOCOL:
        return <MachineProtocol {...p} />;
      case MODES.MACHINE_CONNECTION_METHOD:
        return <MachineConnectionMethod {...p} />;
      case MODES.SEARCH_SETTINGS:
        return <SearchSettings {...p} />;
      case MODES.IP_RANGE_DETAILS:
        return <IpRangeDetails {...p} />;
      case MODES.CREATE_IP_RANGE:
        return <CreateIpRange {...p} />;
      case MODES.CONNECTION:
        return <Connection {...p} />;
      case MODES.SAME_MACHINE:
        return <SameMachine {...p} />;
      default:
        return null;
    }
  };

  const machineConnector = connector?.connectorDetails?.status?.find((c) => c.workerType === workerType.MACHINES) || {};
  if (!connector?.connectorDetails?.connectorId || machineConnector?.status !== workerTypeStatusNames.Online) {
    return <ConnectorStatus {...{ ...props, goBack }} />;
  }

  return renderComponent();
};

const mapStateToProps = ({ machines, connector, membership: { connectorIpRange, ipScanUpdatedOn, ipScanStatus, networkScanIsEnabled } }) => ({
  machines,
  connectorIpRange,
  ipScanUpdatedOn,
  ipScanStatus,
  connector,
  networkScanIsEnabled,
});

export default connect(mapStateToProps, {
  availableMachines,
  validateMachineOnboardingEndpoint,
  startMachineOnboardingSession,
  validateMachineOnboardingByAutomaticControl,
  validateMachineOnboardingByBrand,
  validateMachineOnboardingByConnectionMethod,
  addSelectedMachines,
  getMachines,
  addMachine,
  clearMachines,
  getMachineBrand,
  getMachineConnectionMethod,
  getConnectorIpRange,
  configureIpRangeForScan,
  removeIpRangeFromScan,
  requestIpRangeScan,
  connectorIpScanStatus,
  getMachineFromHistory,
  getDemoMachineType,
  configureNetworkScan,
})(ColorWrapper(AddMachine));
